<template>
  <div
    class="modal"
    :style="{ 'z-index': zIndex }"
    @click.self="closeOnBackdropClick ? $emit('cancel') : () => {}"
    tabindex="0"
  >
    <div class="modal__wrap">
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw, Raw } from 'vue';
import shortcutMixin from '@shortcuts/shortcutMixin';
import { modalZIndex } from '@sass/globals.module.scss';

export default defineComponent({
  emits: { cancel: null, confirm: null },
  name: 'Modal',

  mixins: [shortcutMixin],

  props: {
    closeOnBackdropClick: {
      type: Boolean,
      default: true,
      required: false,
    },

    zIndex: {
      type: Number,
      default: parseInt(modalZIndex, 10),
    },
  },

  data(): {
    previouslyActiveElement: Raw<Element>;
  } {
    return {
      previouslyActiveElement: null,
    };
  },

  shortcuts() {
    return {
      cancel: () => {
        this.$emit('cancel');
      },

      confirm: () => {
        this.$emit('confirm');
      },
    };
  },

  created() {
    if (document.activeElement) {
      this.previouslyActiveElement = markRaw(document.activeElement);
    }
  },

  mounted() {
    // If the modal content has no focussed element,
    // focus the modal so that tabbing goes directly into the first focussable input-
    if (!this.$el.contains(document.activeElement)) {
      this.$el.focus();
    }
  },

  unmounted() {
    setTimeout(() => {
      if (this.previouslyActiveElement && this.previouslyActiveElement.isConnected) {
        // Focus the previously focussed element
        (this.previouslyActiveElement as HTMLElement).focus({ preventScroll: true });
      }
    }, 10);
  },
});
</script>

<style lang="scss">
@use 'settings/settings';

.modal {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  z-index: settings.$modalZIndex;
  display: grid;
  align-items: center;
  justify-content: stretch;
  background: rgba(0, 0, 0, 0.6);
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
}

// if we use padding instead of a margin, chrome does not display the padding at the bottom if the content gets too long
// so the wrap handles the spacing to the sides and things in overlays may be styled with width and max-width without problems
.modal__wrap {
  pointer-events: none;
  margin: 64px;
  display: flex;
  justify-content: center;

  > * {
    pointer-events: all;
  }
}
</style>
